import { Link } from "react-router-dom";
import type { BlogPostListDto } from "@/types";
import { formatDate, formatReadingTime } from "@/lib/utils";
import { useLanguage } from "@/context/LanguageContext";
import {
  CalendarIcon,
  ClockIcon,
  ArrowUpRightIcon,
} from "@heroicons/react/24/solid";

interface BlogPostCardProps {
  post: BlogPostListDto;
  className?: string;
}

const BlogPostCard: React.FC<BlogPostCardProps> = ({
  post,
  className = "",
}) => {
  const { language } = useLanguage();

  return (
    <Link
      to={`/blog/${post.slug}`}
      className={`
        group relative flex h-full flex-col overflow-hidden rounded-2xl 
        border border-gray-200 bg-white
        transition-all duration-300 ease-out
        hover:-translate-y-1 hover:shadow-xl hover:shadow-gray-200/50 
        ${className}
      `}
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-gray-100 ">
        {post.featuredImageUrl ? (
          <img
            src={post.featuredImageUrl}
            alt={post.title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 will-change-transform group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-gray-400">
            <span className="text-sm">No Image</span>
          </div>
        )}

        {post.categoryName && (
          <div className="absolute left-4 top-4">
            <span className="inline-flex items-center rounded-lg bg-white/90 px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-gray-900 shadow-sm backdrop-blur-md">
              {post.categoryName}
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col p-5">
        <h3 className="mb-2 line-clamp-2 text-xl font-bold leading-tight tracking-tight text-gray-900 group-hover:text-primary">
          {post.title}
        </h3>

        {post.excerpt && (
          <p className="mb-6 line-clamp-2 text-sm text-gray-500">
            {post.excerpt}
          </p>
        )}

        <div className="mt-auto border-t border-gray-100 pt-4">
          <div className="flex items-center gap-3">
            <div className="relative h-9 w-9 shrink-0 overflow-hidden rounded-full ring-2 ring-white">
              {post.authorAvatarUrl ? (
                <img
                  src={post.authorAvatarUrl}
                  alt={post.authorName || "Author"}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-gray-200 text-xs font-bold text-gray-500">
                  {post.authorName?.charAt(0) || "?"}
                </div>
              )}
            </div>

            <div className="flex flex-1 flex-col justify-center min-w-0">
              <p className="truncate text-sm font-semibold text-gray-900">
                {post.authorName}
              </p>

              <div className="flex items-center gap-2 text-xs text-gray-500">
                <div className="flex items-center gap-1 whitespace-nowrap">
                  <CalendarIcon className="h-3 w-3" />
                  <span>{formatDate(post.publishedAt, language)}</span>
                </div>

                {post.readingTimeMinutes > 0 && (
                  <>
                    <span className="hidden sm:inline-block text-gray-300">
                      &middot;
                    </span>

                    <div className="hidden sm:flex items-center gap-1 whitespace-nowrap">
                      <ClockIcon className="h-3 w-3" />
                      <span>{formatReadingTime(post.readingTimeMinutes)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="shrink-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              <ArrowUpRightIcon className="h-5 w-5 text-gray-400" />
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default BlogPostCard;
